import * as Api from '@ViewModels';
import salesforceLogoImageUrl from '../../web/assets/logo_salesforce.svg';
import xeroLogoUrl from '../../web/assets/logo_xero_blue.png';
import quickbooksOnlineLogomarkImageUrl from '../../web/assets/logomark_quickbooks_online.svg';
// import { integrationSources } from '../../extViewmodels/Utils';

/**
 * Checks if contact is from enabled integration
 * @source Original location: UiUtils.ts.backup line 1716
 */
export const contactIsFromEnabledIntegration = (
	contact: Api.ContactViewModel,
	integrations: Api.IAccountIntegrations
): boolean => {
	const source = contact.source;

	return (
		!!source &&
		!!integrations &&
		((source === Api.IntegrationSources.HawkSoft && integrations.hawkSoft?.enabled) ||
			(source === Api.IntegrationSources.AMS360 && integrations.ams360?.enabled) ||
			(source === Api.IntegrationSources.Redtail && integrations.redtail?.enabled) ||
			(source === Api.IntegrationSources.Eclipse && integrations.eclipse?.enabled) ||
			(source === Api.IntegrationSources.Clio && integrations.clio?.enabled) ||
			(source === Api.IntegrationSources.EpicCsv && integrations.epicCsv?.enabled) ||
			(source === Api.IntegrationSources.EzLynxCsv && integrations.ezLynxCsv?.enabled) ||
			(source === Api.IntegrationSources.QQCatalyst && integrations.qqCatalyst?.enabled) ||
			(source === Api.IntegrationSources.Wealthbox && integrations.wealthbox?.enabled) ||
			source === Api.IntegrationSources.InternalLevitate ||
			(source === Api.IntegrationSources.MergeAccounting && integrations.mergeAccounting?.enabled) ||
			(source === Api.IntegrationSources.MergeCrm && integrations.mergeCrm?.enabled) ||
			(source === Api.IntegrationSources.GeneralCsv && integrations.generalCsv?.enabled) ||
			(source === Api.IntegrationSources.DonorPerfect && integrations.donorPerfect?.enabled))
	);
};

/**
 * Gets contact source
 * @source Original location: UiUtils.ts.backup line 1742
 */
export const getContactSource = (contact: Api.ContactViewModel, integrations: Api.IAccountIntegrations) => {
	const source = contact.source;

	if (source === Api.IntegrationSources.MergeAccounting) {
		return getMergeLinkProviderDisplayName(integrations);
	}

	if (source === Api.IntegrationSources.MergeCrm) {
		return getMergeLinkProviderDisplayName(integrations);
	}

	if (source === Api.IntegrationSources.EpicCsv) {
		return 'Epic';
	}

	if (source === Api.IntegrationSources.EzLynxCsv) {
		return 'EzLynx';
	}

	if (source === Api.IntegrationSources.GeneralCsv) {
		return integrations.generalCsv.generalCsvProvider;
	}

	return source;
};

const titleCase = (value: string) => {
	if (!value) return '';
	const newValue = value.toLowerCase();
	return `${newValue[0].toUpperCase()}${newValue.slice(1)}`;
};

/**
 * Gets integration name by key
 * @source Original location: UiUtils.ts.backup line 1774
 */
export const getIntegrationNameByKey = (
	key: keyof Api.IAccountIntegrations,
	integrations: Api.IAccountIntegrations
): string => {
	// I do not like magic strings, but I could not get anything else working
	if (key === 'mergeCrm') return getMergeLinkProviderDisplayName(integrations);
	if (key === 'mergeAccounting') return getMergeLinkProviderDisplayName(integrations);

	if (key === 'hawkSoft') return 'HawkSoft';
	if (key === 'ams360') return 'AMS360';
	if (key === 'redtail') return 'Redtail';
	if (key === 'eclipse') return 'Eclipse';
	if (key === 'clio') return 'Clio';
	if (key === 'epicCsv') return 'Epic';
	if (key === 'ezLynxCsv') return 'EzLynx';
	if (key === 'qqCatalyst') return 'QQ Catalyst';
	if (key === 'wealthbox') return 'Wealthbox';
	if (key === 'donorPerfect') return 'DonorPerfect';

	return titleCase(key);
};

/**
 * Gets logo URL for integration provider
 * @source Original location: UiUtils.ts.backup line 2402
 */
export const getLogoUrlForIntegrationProvider = (provider: string) => {
	const providerName = provider?.toLowerCase();
	switch (providerName) {
		case 'salesforce': {
			return salesforceLogoImageUrl;
		}
		case 'quickbooksonline': {
			return quickbooksOnlineLogomarkImageUrl;
		}
		case 'xero': {
			return xeroLogoUrl;
		}
		default: {
			return null;
		}
	}
};

/**
 * Gets merge link provider display name
 * @source Original location: UiUtils.ts.backup line 2420
 */
export const getMergeLinkProviderDisplayName = (accountIntegrations: Api.IAccountIntegrations) => {
	if (accountIntegrations?.mergeAccounting) {
		switch (accountIntegrations.mergeAccounting.mergeAccountingProvider) {
			case Api.MergeAccountingProvider.QuickBooksOnline:
				return 'QuickBooks Online';
			case Api.MergeAccountingProvider.Xero:
				return 'Xero';
			default:
				return 'Merge Accounting';
		}
	}

	if (accountIntegrations?.mergeCrm) {
		switch (accountIntegrations.mergeCrm.mergeCrmProvider) {
			case Api.MergeCrmProvider.Salesforce:
				return 'Salesforce';
			default:
				return 'Merge CRM';
		}
	}

	return 'Merge';
};

/**
 * Gets display name for V2 integration provider enum
 */
export const getIntegrationProviderDisplayName = (provider: Api.IntegrationProvider): string => {
	const displayNames: Partial<Record<Api.IntegrationProvider, string>> = {
		[Api.IntegrationProvider.HawkSoft]: 'HawkSoft',
		[Api.IntegrationProvider.AMS360]: 'AMS360',
		[Api.IntegrationProvider.Redtail]: 'Redtail',
		[Api.IntegrationProvider.Eclipse]: 'Eclipse',
		[Api.IntegrationProvider.Clio]: 'Clio',
		[Api.IntegrationProvider.EzLynxCsv]: 'EzLynx',
		[Api.IntegrationProvider.EpicCsv]: 'Epic',
		[Api.IntegrationProvider.QQCatalyst]: 'QQ Catalyst',
		[Api.IntegrationProvider.HubSpot]: 'HubSpot',
		[Api.IntegrationProvider.Wealthbox]: 'Wealthbox',
		[Api.IntegrationProvider.MyCase]: 'MyCase',
		[Api.IntegrationProvider.DonorPerfect]: 'DonorPerfect',
		[Api.IntegrationProvider.PracticePanther]: 'PracticePanther',
		[Api.IntegrationProvider.MicrosoftDynamics365Sales]: 'Microsoft Dynamics 365',
		[Api.IntegrationProvider.Salesforce]: 'Salesforce',
		[Api.IntegrationProvider.ServiceTitan]: 'ServiceTitan',
		[Api.IntegrationProvider.QuickBooksOnline]: 'QuickBooks Online',
	};
	return displayNames[provider] || provider;
};

/**
 * Gets display names for all enabled integrations (both V1 and V2)
 * @param v1Integrations - V1 integrations from account.integrations
 * @param v2Integrations - V2 integrations from useGetExternalIntegrationsQuery
 * @returns Array of unique display names for enabled integrations
 */
export const getEnabledIntegrationDisplayNames = (
	v1Integrations: Api.IAccountIntegrations | undefined,
	v2Integrations: Api.IExternalIntegration[] | undefined
): string[] => {
	const enabledNames = new Set<string>();

	// Check V1 integrations
	if (v1Integrations) {
		const v1Keys: (keyof Api.IAccountIntegrations)[] = [
			'hawkSoft',
			'ams360',
			'redtail',
			'eclipse',
			'clio',
			'epicCsv',
			'ezLynxCsv',
			'qqCatalyst',
			'wealthbox',
			'donorPerfect',
			'mergeAccounting',
			'mergeCrm',
			'myCase',
			'practicePanther',
			'hubSpot',
		];

		for (const key of v1Keys) {
			const integration = v1Integrations[key] as Api.IIntegration | undefined;
			if (integration?.enabled) {
				enabledNames.add(getIntegrationNameByKey(key, v1Integrations));
			}
		}
	}

	// Check V2 integrations
	if (v2Integrations) {
		for (const integration of v2Integrations) {
			if (integration.enabled && integration.provider !== Api.IntegrationProvider.None) {
				enabledNames.add(getIntegrationProviderDisplayName(integration.provider));
			}
		}
	}

	return Array.from(enabledNames);
};

/**
 * Determines if a key fact can be edited based on integration sources and data origin settings
 * @source Original location: UiUtils.ts.backup line 1819
 * @param keyFact - The key fact to check
 * @param contact - Contact information with source and dataOriginEnabled
 * @param integrations - The account's v1 integration settings
 * @param integrationSources - Array of integration source types (from Utils)
 * @param externalIntegrations - The account's v2 integration settings (optional)
 * @returns true if the key fact can be edited/deleted, false otherwise
 */
export const canEditKeyFact = (
	keyFact: Api.IKeyFact,
	contact: { source?: Api.IntegrationSources; dataOriginEnabled?: boolean },
	integrations: Api.IAccountIntegrations,
	integrationSources: string[],
	externalIntegrations?: Partial<Api.IExternalIntegration>[]
): boolean => {
	const dataOriginEnabled = contact.dataOriginEnabled;
	const keyFactSource = keyFact.source?.source;

	// Check if key fact came from an integration (v1 or v2)
	let cameFromIntegration = keyFactSource && integrationSources.includes(keyFactSource);

	// Also check v2 integrations - some providers only exist in v2 (e.g., HubSpot, Salesforce)
	if (!cameFromIntegration && externalIntegrations && keyFactSource) {
		const v2Integration = externalIntegrations.find(
			integration => integration.provider === (keyFactSource as unknown as Api.IntegrationProvider)
		);
		cameFromIntegration = !!v2Integration;
	}

	// Check if the KEY FACT's specific integration source is enabled
	let isKeyFactSourceEnabled = false;
	let foundV2Integration = false;

	// First check v2 integrations (IExternalIntegration[]) if provided
	if (externalIntegrations && keyFactSource) {
		// IntegrationProvider enum values match IntegrationSources values (e.g., 'HawkSoft', 'AMS360')
		const v2Integration = externalIntegrations.find(
			integration => integration.provider === (keyFactSource as unknown as Api.IntegrationProvider)
		);
		foundV2Integration = !!v2Integration;
		isKeyFactSourceEnabled = v2Integration?.enabled ?? false;
	}

	// Fall back to v1 integrations (IAccountIntegrations) if v2 not found
	if (!foundV2Integration && keyFactSource && integrations) {
		isKeyFactSourceEnabled =
			(keyFactSource === Api.IntegrationSources.HawkSoft && integrations.hawkSoft?.enabled) ||
			(keyFactSource === Api.IntegrationSources.AMS360 && integrations.ams360?.enabled) ||
			(keyFactSource === Api.IntegrationSources.Redtail && integrations.redtail?.enabled) ||
			(keyFactSource === Api.IntegrationSources.Eclipse && integrations.eclipse?.enabled) ||
			(keyFactSource === Api.IntegrationSources.Clio && integrations.clio?.enabled) ||
			(keyFactSource === Api.IntegrationSources.EpicCsv && integrations.epicCsv?.enabled) ||
			(keyFactSource === Api.IntegrationSources.EzLynxCsv && integrations.ezLynxCsv?.enabled) ||
			(keyFactSource === Api.IntegrationSources.QQCatalyst && integrations.qqCatalyst?.enabled) ||
			(keyFactSource === Api.IntegrationSources.Wealthbox && integrations.wealthbox?.enabled) ||
			keyFactSource === Api.IntegrationSources.InternalLevitate ||
			(keyFactSource === Api.IntegrationSources.MergeAccounting && integrations.mergeAccounting?.enabled) ||
			(keyFactSource === Api.IntegrationSources.MergeCrm && integrations.mergeCrm?.enabled) ||
			(keyFactSource === Api.IntegrationSources.GeneralCsv && integrations.generalCsv?.enabled) ||
			(keyFactSource === Api.IntegrationSources.DonorPerfect && integrations.donorPerfect?.enabled);
	}

	// If key fact is from an enabled integration, it cannot be edited
	// (dataOriginEnabled === false or undefined means the feature is not turned on)
	if (cameFromIntegration && dataOriginEnabled !== true && isKeyFactSourceEnabled) {
		return false;
	}

	// If data origin is not enabled and key fact has no integration source (user created) or is from CSV import, allow editing
	if ((dataOriginEnabled !== true && !keyFactSource) || keyFact.source?.source === Api.IntegrationSources.CsvImport) {
		return true;
	}

	// If key fact is from an integration but that integration is disabled, allow editing
	if (cameFromIntegration && dataOriginEnabled !== true && !isKeyFactSourceEnabled) {
		return true;
	}

	// Default to not allowing edits for other cases (data origin enabled with a source)
	return false;
};
